/**
 * Client HTTP typé du back-office (DECISIONS §4.1 : enveloppes `{ data, meta }` / `{ error, meta }`).
 *
 * Responsabilités, et rien de plus :
 * - préfixer par `env.apiBaseUrl` et injecter `Authorization` depuis le store **en mémoire** ;
 * - décoder les enveloppes avec le décodeur de `@elemora/shared` (une seule vérité de contrat) ;
 * - sur `401`, tenter **un seul** `POST /auth/refresh` (cookie `HttpOnly`) puis rejouer la requête
 *   une fois ; un second échec vide la session et la garde de route ramène à la connexion ;
 * - ne jamais laisser fuir une exception réseau brute : tout devient `ErreurApi`.
 *
 * Ce que ce module ne fait **jamais** : écrire le jeton dans un stockage persistant, ni décider
 * d'un droit (le serveur répond `403`, l'interface se contente d'afficher).
 */
import { parseEnvelope, refreshResponseSchema } from '@elemora/shared/api';
import { API_PATHS, AUTHORIZATION_HEADER, BEARER_PREFIX } from '@elemora/shared/constants';

import { ErreurApi } from './erreurs';
import { env } from '../app/env';
import { lireJeton, useSessionAdmin } from '../session/session-admin';

import type { z } from 'zod';

export interface OptionsRequete<T> {
  readonly methode: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  /** Chemin relatif à `env.apiBaseUrl`, ex. `/admin/cards`. */
  readonly chemin: string;
  /** Schéma zod de la charge utile attendue (déjà livré par `@elemora/shared`). */
  readonly schema: z.ZodType<T>;
  /** Corps JSON sérialisé tel quel. */
  readonly corps?: unknown;
  /** `false` pour la route de connexion (aucun jeton à joindre). Défaut : `true`. */
  readonly authentifiee?: boolean;
  readonly signal?: AbortSignal;
}

type OptionsTransport = Omit<OptionsRequete<unknown>, 'schema'>;

/** Construit les en-têtes d'une requête. */
function construireEntetes(options: OptionsTransport, jeton: string | null): Headers {
  const entetes = new Headers({ Accept: 'application/json' });
  if (options.corps !== undefined) entetes.set('Content-Type', 'application/json');
  if (options.authentifiee !== false && jeton !== null) {
    entetes.set(AUTHORIZATION_HEADER, `${BEARER_PREFIX}${jeton}`);
  }
  return entetes;
}

/** Lit le corps d'une réponse sans jamais lever : un corps illisible vaut `undefined`. */
async function lireCorps(reponse: Response): Promise<unknown> {
  const texte = await reponse.text();
  if (texte.length === 0) return undefined;
  try {
    return JSON.parse(texte) as unknown;
  } catch {
    return undefined;
  }
}

/** Exécute l'appel réseau, en convertissant un échec de transport en `ErreurApi`. */
async function envoyer(
  options: OptionsTransport,
  jeton: string | null,
  signal: AbortSignal | undefined,
): Promise<Response> {
  try {
    return await fetch(`${env.apiBaseUrl}${options.chemin}`, {
      method: options.methode,
      headers: construireEntetes(options, jeton),
      credentials: 'include',
      ...(options.corps === undefined ? {} : { body: JSON.stringify(options.corps) }),
      ...(signal === undefined ? {} : { signal }),
    });
  } catch (erreur) {
    throw new ErreurApi({
      code: 'INTERNAL_ERROR',
      message: erreur instanceof Error ? erreur.message : 'Échec réseau',
      statutHttp: 0,
      reseau: true,
    });
  }
}

/** Rafraîchissement en cours : plusieurs `401` simultanés ne déclenchent qu'un seul refresh. */
let rafraichissementEnCours: Promise<string | null> | null = null;

async function effectuerRafraichissement(): Promise<string | null> {
  try {
    const reponse = await fetch(`${env.apiBaseUrl}${API_PATHS.auth.refresh}`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      credentials: 'include',
    });
    if (!reponse.ok) {
      useSessionAdmin.getState().deconnecter();
      return null;
    }
    const resultat = parseEnvelope(await lireCorps(reponse), refreshResponseSchema);
    if (!resultat.ok) {
      useSessionAdmin.getState().deconnecter();
      return null;
    }
    useSessionAdmin.getState().definirJeton(resultat.data.accessToken);
    return resultat.data.accessToken;
  } catch {
    useSessionAdmin.getState().deconnecter();
    return null;
  }
}

/** Rafraîchit le jeton d'accès (un seul appel à la fois, rejouable sans risque). */
export function rafraichirAccessToken(): Promise<string | null> {
  if (rafraichissementEnCours === null) {
    rafraichissementEnCours = effectuerRafraichissement().finally(() => {
      rafraichissementEnCours = null;
    });
  }
  return rafraichissementEnCours;
}

/** Envoie la requête ; sur `401`, rafraîchit **une fois** et rejoue **une fois**. */
async function envoyerAvecReprise(
  options: OptionsTransport,
  signal: AbortSignal | undefined,
): Promise<Response> {
  let reponse = await envoyer(options, lireJeton(), signal);
  if (reponse.status === 401 && options.authentifiee !== false) {
    const jeton = await rafraichirAccessToken();
    if (jeton !== null) reponse = await envoyer(options, jeton, signal);
  }
  return reponse;
}

/**
 * Appelle l'API et renvoie la charge utile décodée.
 *
 * Toute réponse hors contrat (HTML d'un proxy en panne, `meta` manquant…) devient une
 * `INTERNAL_ERROR` explicite : jamais un `undefined` silencieux dans un écran.
 */
export async function requete<T>(options: OptionsRequete<T>): Promise<T> {
  const reponse = await envoyerAvecReprise(options, options.signal);
  const resultat = parseEnvelope(await lireCorps(reponse), options.schema);
  if (!resultat.ok) {
    throw new ErreurApi({
      code: resultat.code,
      message: `Réponse ${String(reponse.status)} (${resultat.code})`,
      statutHttp: reponse.status,
      messageServeur: resultat.message,
      ...(resultat.details === undefined ? {} : { details: resultat.details }),
      requestId: resultat.meta.requestId,
    });
  }
  return resultat.data;
}
